"use client";

import { useEffect, useRef, useState, type MouseEvent } from "react";
import { privacyPolicyData } from "@/data/legal/privacyPolicyData";
import { termsConditionsData } from "@/data/legal/termsConditionsData";
import SeoHead from "@/components/SEO/SeoHead";
import BreadcrumbWidget from "@/components/Widgets/BreadcrumbWidget";
import EnrollButton from "@/components/LeadForm/EnrollButton";
import WhatsAppButton from "@/components/WhatsApp/WhatsAppButton";
import { openWhatsApp, whatsappMessages } from "@/utils/whatsapp";
import { FORM_CONTEXTS } from "@/utils/innovlistWidget";

interface Subsection {
  heading: string;
  content?: string;
  items?: string[];
}

interface Section {
  id: string;
  heading: string;
  content?: string;
  items?: string[];
  subsections?: Subsection[];
}

interface LegalData {
  slug: string;
  seo: {
    title: string;
    description: string;
    canonical?: string;
  };
  hero: {
    heading: string;
    lastUpdated: string;
  };
  sections: Section[];
}

const dataByPage: Record<string, LegalData> = {
  privacy: privacyPolicyData as LegalData,
  terms: termsConditionsData as LegalData,
};

export default function TermsConditionsPage() {
  const data = dataByPage.terms;
  const legalWhatsAppMessage = "Hi, I have a question about Seasoft Classes policies.";
  const [activeSection, setActiveSection] = useState<string>(data.sections?.[0]?.id || "");
  const detailsRef = useRef<HTMLDetailsElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );

    data.sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [data.sections]);

  const handleSectionJump = (event: MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    event.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    if (detailsRef.current?.open) {
      detailsRef.current.open = false;
    }
  };

  return (
    <>
      <SeoHead
        title={data.seo.title}
        description={data.seo.description}
        canonical={data.seo.canonical}
      />

      <section className="bg-[#0a1628] text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold">{data.hero.heading}</h1>
          <p className="text-gray-400 mt-2">Last updated: {data.hero.lastUpdated}</p>
        </div>
      </section>

      <BreadcrumbWidget
        crumbs={[
          { label: "Home", href: "/" },
          { label: "Terms & Conditions", href: "" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="lg:grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3">
            <details
              ref={detailsRef}
              className="mb-8 bg-gray-50 rounded-xl border border-gray-200 lg:hidden"
            >
              <summary className="px-6 py-4 font-semibold cursor-pointer text-gray-800">
                Jump to Section ▾
              </summary>
              <div className="px-6 pb-4 space-y-2">
                {data.sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="block text-sm text-blue-600 py-1 hover:underline"
                    onClick={(event) => handleSectionJump(event, section.id)}
                  >
                    {section.heading}
                  </a>
                ))}
              </div>
            </details>

            {data.sections.map((section) => (
              <section key={section.id} id={section.id} className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 pb-3 mb-6 border-b-2 border-blue-600">
                  {section.heading}
                </h2>

                {section.content ? (
                  <p className="text-gray-700 leading-relaxed">{section.content}</p>
                ) : null}

                {section.items ? (
                  <ul className="space-y-3 mt-4">
                    {section.items.map((item, index) => (
                      <li key={index} className="flex gap-3 text-gray-700 leading-relaxed">
                        <span className="text-blue-600 font-bold mt-1 flex-shrink-0">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {section.subsections
                  ? section.subsections.map((subsection) => (
                      <div key={subsection.heading} className="mt-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">
                          {subsection.heading}
                        </h3>
                        {subsection.content ? (
                          <p className="text-gray-700 leading-relaxed">{subsection.content}</p>
                        ) : null}
                        {subsection.items ? (
                          <ul className="space-y-3 mt-4">
                            {subsection.items.map((item, index) => (
                              <li key={index} className="flex gap-3 text-gray-700 leading-relaxed">
                                <span className="text-blue-600 font-bold mt-1 flex-shrink-0">•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ))
                  : null}
              </section>
            ))}

            <div className="bg-blue-50 rounded-2xl p-8 mt-12 text-center border border-blue-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Have Questions About Our Policies?
              </h3>
              <p className="text-gray-600 mb-6">
                Our team is happy to clarify anything. Get in touch with us directly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <EnrollButton
                  label="Contact Us"
                  variant="primary"
                  size="md"
                  context={FORM_CONTEXTS.LEGAL_PAGE_CTA || "legal_page_cta"}
                  icon={false}
                />
                <WhatsAppButton
                  variant="outline"
                  size="md"
                  label="Chat on WhatsApp"
                  message={legalWhatsAppMessage}
                  context="legal_page"
                />
              </div>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24 bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider">
                Table of Contents
              </h3>
              <nav className="space-y-2">
                {data.sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={(event) => handleSectionJump(event, section.id)}
                    className={`block text-sm py-1 px-3 rounded transition-colors ${
                      activeSection === section.id
                        ? "bg-blue-600 text-white font-medium"
                        : "text-gray-600 hover:text-blue-600 hover:bg-blue-50"
                    }`}
                  >
                    {section.heading}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
